import { Box, SxProps, Theme } from '@mui/material';

export type ProgressStep = Readonly<{
  key: string; // Question key (e.g., 'ROLE_SELECTION')
  index: number; // Position in visible steps array (0-based)
  isCompleted: boolean;
  isCurrent: boolean;
  isClickable: boolean; // Can navigate back to this step
}>;

type OnboardingProgressBarProps = Readonly<{
  steps: ProgressStep[];
  onStepClick: (questionIndex: number) => void;
}>;

export const OnboardingProgressBar = ({ steps, onStepClick }: OnboardingProgressBarProps) => {
  if (steps.length === 0) return null;

  return (
    <Box sx={containerStyle}>
      <Box sx={progressBarStyle}>
        {steps.map((step, idx) => (
          <Box
            key={step.key}
            id={`onboarding-progress-step-${idx}`}
            sx={segmentStyle(step.isCurrent, step.isClickable)}
            onClick={() => {
              if (step.isClickable) {
                onStepClick(step.index);
              }
            }}
            role={step.isClickable ? 'button' : undefined}
            tabIndex={step.isClickable ? 0 : undefined}
            onKeyDown={(e) => {
              if (step.isClickable && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onStepClick(step.index);
              }
            }}
            aria-label={`Step ${idx + 1} of ${steps.length}${step.isCurrent ? ' (current)' : ''}${step.isCompleted ? ' (completed)' : ''}`}
            aria-current={step.isCurrent ? 'step' : undefined}
          />
        ))}
      </Box>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  padding: backrs.spacing['spacing-xl'],
  paddingBottom: backrs.spacing['spacing-3xl'],
  mt: 'auto',
  backgroundColor: backrs.colors['Colors/Base/White'],
});

const progressBarStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
  maxWidth: '360px',
  width: '100%',
});

const segmentStyle =
  (isCurrent: boolean, isClickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    flex: 1,
    height: '8px',
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: isCurrent
      ? backrs.colors['Colors/Foreground/fg-brand-primary-alt']
      : backrs.colors['Colors/Background/bg-quaternary'],
    cursor: isClickable ? 'pointer' : 'default',
    transition: 'background-color 0.2s ease',
    '&:hover': isClickable
      ? {
          backgroundColor: isCurrent
            ? backrs.colors['Colors/Foreground/fg-brand-primary-alt']
            : backrs.colors['Colors/Background/bg-tertiary'],
        }
      : {},
    '&:focus-visible': {
      outline: `2px solid ${backrs.colors['Colors/Foreground/fg-brand-primary-alt']}`,
      outlineOffset: '2px',
    },
  });
